import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

export default function OptionalTopicsPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [draftName, setDraftName] = useState('');
    const [busy, setBusy] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/optional-topics`, authHeaders());
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const add = async (e) => {
        e?.preventDefault?.();
        if (!draftName.trim()) return;
        setBusy(true);
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/optional-topics`,
                { name: draftName.trim() },
                authHeaders()
            );
            setDraftName('');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (item) => {
        setEditId(item._id);
        setEditName(item.name);
    };

    const cancelEdit = () => setEditId(null);

    const saveEdit = async (id) => {
        try {
            await axios.patch(
                `${API_URL}/api/admin/optional-topics/${id}`,
                { name: editName.trim() },
                authHeaders()
            );
            cancelEdit();
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete topic', message: 'Delete this topic?', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/optional-topics/${id}`, authHeaders());
            await load();
        } catch {
            setError('Failed to delete.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Optional topics</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Topics admin can pick per tutoring class (e.g. "Power & Conflict",
                        "An Inspector Calls").
                    </Typography>
                    
                </Box>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Optional topics are extras a tutor can offer inside a class — bonus modules, competition prep, additional workshops.<br />
                            <strong>2.</strong> Click <strong>New topic</strong> to add one — name, description, which classes it's offered to.<br />
                            <strong>3.</strong> Edit or delete using the row icons. Removing a topic doesn't affect classes that already ran with it.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <Box component="form" onSubmit={add} sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="New topic"
                        size="small"
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        sx={{ flex: 1 }}
                    />
                    <Button type="submit" variant="contained" disabled={busy || !draftName.trim()}>
                        Add
                    </Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Topic</TableCell>
                                <TableCell align="right" sx={{ width: 140 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item._id}>
                                    <TableCell>
                                        {editId === item._id ? (
                                            <TextField
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                size="small"
                                                fullWidth
                                                autoFocus
                                            />
                                        ) : (
                                            item.name
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {editId === item._id ? (
                                            <>
                                                <IconButton onClick={() => saveEdit(item._id)} color="primary"><SaveIcon /></IconButton>
                                                <IconButton onClick={cancelEdit}><CloseIcon /></IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton onClick={() => startEdit(item)}><EditIcon /></IconButton>
                                                <IconButton onClick={() => remove(item._id)}><DeleteIcon /></IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No topics yet — add one above.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Container>
    );
}